import React, { useState, useCallback, useEffect } from 'react';
import JSZip from 'jszip';
import {
  GeneratedPost,
  TemplateSummary,
  Template,
  TextElement,
  PreviewFormat,
  BrandInfo,
  TemplateColorMode,
} from '@agency-smm/shared';
import { apiClient } from '../../services/apiClient';
import {
  renderCompositeAsBlob,
  renderCompositeThumbnail,
  clearImageCache,
  RenderOptions,
  ExportQuality,
  EXPORT_PRESETS,
} from '../../utils/canvasRenderer';

export function useTemplateAndThumbnails(
  workspaceId: string | undefined,
  posts: GeneratedPost[],
  workspace: { name?: string; logoScale?: number; logoOffsetX?: number; logoOffsetY?: number; selectedTemplateIds?: string[] | null } | null,
  setPosts?: React.Dispatch<React.SetStateAction<GeneratedPost[]>>,
) {
  const [templates, setTemplates] = useState<TemplateSummary[]>([]);
  const [bulkTemplateId, setBulkTemplateId] = useState<string>('');
  const [bulkTemplate, setBulkTemplate] = useState<Template | null>(null);
  const [solidTemplate, setSolidTemplate] = useState<Template | null>(null);
  const [templatePool, setTemplatePool] = useState<Template[]>([]);
  const [postTemplateOverrides, setPostTemplateOverrides] = useState<Record<string, string>>({});
  const [postFormatOverrides, setPostFormatOverrides] = useState<Record<string, PreviewFormat>>({});
  const [compositeThumbnails, setCompositeThumbnails] = useState<Record<string, string>>({});
  const [workspaceLogoUrl, setWorkspaceLogoUrl] = useState<string | undefined>(undefined);
  const [brandColors, setBrandColors] = useState<string[]>([]);
  const [brandFonts, setBrandFonts] = useState<string[]>([]);
  const [isBulkProcessing, setIsBulkProcessing] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ current: 0, total: 0 });
  const [error, setError] = useState('');

  // Clear image cache on unmount
  useEffect(() => {
    return () => { clearImageCache(); };
  }, []);

  const fetchTemplates = async () => {
    try {
      const data = await apiClient.get<TemplateSummary[]>(`/api/workspaces/${workspaceId}/templates`);
      setTemplates(data);

      const stockPool = data.filter((t) =>
        t.suitableFor === 'stock' || t.suitableFor === 'all' || (!t.suitableFor && !t.name?.toLowerCase().includes('solid'))
      );
      const pastelPool = data.filter((t) =>
        t.suitableFor === 'pastel' || t.suitableFor === 'all'
      );

      const solidTpl = pastelPool.find((t) => t.isGlobal) || pastelPool[0];
      if (solidTpl) {
        try {
          const fullSolidTemplate = await apiClient.get<Template>(`/api/workspaces/${workspaceId}/templates/${solidTpl.id}`);
          setSolidTemplate(fullSolidTemplate);
        } catch {
          console.error('Failed to fetch solid template');
        }
      }

      // Filter out inactive non-global templates from the pool
      const activeTemplates = data.filter((t) => t.isGlobal || t.isActive !== false);
      const globalTemplates = activeTemplates.filter((t) => t.isGlobal);
      const fullTemplatePromises = globalTemplates.map(async (t) => {
        try {
          return await apiClient.get<Template>(`/api/workspaces/${workspaceId}/templates/${t.id}`);
        } catch {
          return null;
        }
      });
      const fullTemplates = (await Promise.all(fullTemplatePromises)).filter(Boolean) as Template[];
      setTemplatePool(fullTemplates);

      if (data.length > 0 && !bulkTemplateId) {
        const defaultTpl = stockPool.find((t) => t.isGlobal) || stockPool[0] || data[0];
        setBulkTemplateId(defaultTpl.id);
      }
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    }
  };

  const fetchWorkspaceLogo = async () => {
    try {
      const logo = await apiClient.get<{ url: string } | null>(`/api/workspaces/${workspaceId}/logo`);
      if (logo) {
        setWorkspaceLogoUrl(logo.url);
      }
    } catch {
      // No logo set
    }
  };

  const fetchBrandInfo = async () => {
    try {
      const info = await apiClient.get<BrandInfo>(`/api/workspaces/${workspaceId}/brand-info`);
      if (info) {
        setBrandColors(info.brandColors || []);
        setBrandFonts(info.brandFonts || []);
      }
    } catch {
      // No brand info set
    }
  };

  /** Compute a deterministic numeric seed from a post ID */
  const computePostSeed = (postId: string): number =>
    postId.split('').reduce((acc, ch) => acc + ch.charCodeAt(0), 0);

  /** Build the brand-color-related RenderOptions fields for a given template + post */
  const buildBrandRenderOptions = useCallback((
    templateForPost: Template,
    post: GeneratedPost,
  ): Partial<RenderOptions> => {
    const colorMode: TemplateColorMode = templateForPost.colorMode || 'fixed';
    if (colorMode === 'fixed' || brandColors.length === 0) {
      return {};
    }
    return {
      brandColors,
      brandFonts: brandFonts.length > 0 ? brandFonts : undefined,
      colorMode,
      postSeed: computePostSeed(post.id),
    };
  }, [brandColors, brandFonts]);

  // Get dynamic text for a post element
  const getDynamicTextForElement = useCallback((
    postId: string,
    dynamicType: string | undefined
  ): string => {
    const post = posts.find((p) => p.id === postId);
    if (!post?.dynamicTexts || !dynamicType) return '';

    switch (dynamicType) {
      case 'headline': return post.dynamicTexts.headline || '';
      case 'tagline': return post.dynamicTexts.tagline || '';
      case 'cta': return post.dynamicTexts.cta || '';
      case 'quote': return post.dynamicTexts.quote || '';
      case 'stat': return post.dynamicTexts.stat || '';
      case 'custom': return post.dynamicTexts.headline || '';
      default: return '';
    }
  }, [posts]);

  const buildDynamicTextsMap = useCallback((
    postId: string,
    template: Template
  ): Record<string, string> => {
    const post = posts.find((p) => p.id === postId);
    if (!post?.dynamicTexts) return {};

    const textsMap: Record<string, string> = {};
    for (const element of template.elements) {
      if (element.elementType === 'text') {
        const textElement = element as TextElement;
        if (textElement.isDynamic && textElement.dynamicType) {
          const dynamicText = getDynamicTextForElement(postId, textElement.dynamicType);
          if (dynamicText) {
            textsMap[element.id] = dynamicText;
          }
        }
      }
    }
    return textsMap;
  }, [posts, getDynamicTextForElement]);

  // Check if a template is suitable for a given image source
  const isTemplateSuitableFor = useCallback((template: { suitableFor?: string }, source: string): boolean => {
    const suitable = template.suitableFor || 'all';
    if (suitable === 'all') return true;
    // comma-separated values: "stock,ai,asset"
    const sources = suitable.split(',').map(s => s.trim());
    return sources.includes(source);
  }, []);

  const getTemplateForPost = useCallback((post: GeneratedPost, fallbackTemplate: Template): Template => {
    const imageSource = post.imageSource || 'stock';

    if (templatePool.length === 0) {
      return fallbackTemplate;
    }

    // Build candidate pool from selected templates or all
    const selectedIds = workspace?.selectedTemplateIds;
    let pool = selectedIds?.length
      ? templatePool.filter((t) => selectedIds.includes(t.id))
      : templatePool;
    if (pool.length === 0) pool = templatePool;

    // Filter by image source compatibility
    let candidates = pool.filter((t) => isTemplateSuitableFor(t, imageSource));

    // If no candidates match the source, try 'all' templates or fallback
    if (candidates.length === 0) {
      candidates = pool.filter((t) => (t.suitableFor || 'all') === 'all');
    }
    if (candidates.length === 0) {
      return fallbackTemplate;
    }

    // Try to match content category affinity
    const category = post.contentCategory;
    if (category && candidates.length > 2) {
      const categoryMatches = candidates.filter((t) => {
        const affinity = t.contentCategoryAffinity;
        return affinity === category;
      });
      if (categoryMatches.length > 0) {
        candidates = categoryMatches;
      }
    }

    // Deterministic variety via post ID hash
    const hash = post.id.split('').reduce((acc, ch) => acc + ch.charCodeAt(0), 0);
    return candidates[hash % candidates.length];
  }, [templatePool, workspace?.selectedTemplateIds, isTemplateSuitableFor]);

  const updateSingleThumbnail = useCallback(async (postId: string, template: Template, forceExact = false) => {
    const post = posts.find((p) => p.id === postId);
    if (!post || !post.imageUrl) return;

    // When forceExact=true (explicit override), use the template as-is
    const templateForPost = forceExact ? template : getTemplateForPost(post, template);
    const dynamicTexts = buildDynamicTextsMap(postId, templateForPost);

    const thumbnailUrl = await renderCompositeThumbnail(
      post.imageUrl, templateForPost, 400,
      {
        dynamicTexts, workspaceLogoUrl,
        logoScale: workspace?.logoScale, logoOffsetX: workspace?.logoOffsetX, logoOffsetY: workspace?.logoOffsetY,
        backgroundOffsetX: post.backgroundOffsetX ?? 0,
        backgroundOffsetY: post.backgroundOffsetY ?? 0,
        backgroundScale: post.backgroundScale ?? 1.0,
        ...buildBrandRenderOptions(templateForPost, post),
      }
    );

    if (thumbnailUrl) {
      setCompositeThumbnails((prev) => ({ ...prev, [postId]: thumbnailUrl }));
    } else {
      setCompositeThumbnails((prev) => {
        const n = { ...prev };
        delete n[postId];
        return n;
      });
    }
  }, [posts, buildDynamicTextsMap, workspaceLogoUrl, workspace?.logoScale, getTemplateForPost, buildBrandRenderOptions]);

  const generateThumbnailPreviews = useCallback(async (template: Template, skipOverrides: boolean = true) => {
    const postsWithImages = posts.filter((p) => p.imageUrl);
    if (postsWithImages.length === 0) return;

    const thumbnails: Record<string, string> = {};
    for (const post of postsWithImages) {
      if (skipOverrides && postTemplateOverrides[post.id]) continue;

      const templateForPost = getTemplateForPost(post, template);
      const dynamicTexts = buildDynamicTextsMap(post.id, templateForPost);

      const thumbnailUrl = await renderCompositeThumbnail(
        post.imageUrl!, templateForPost, 400,
        {
          dynamicTexts, workspaceLogoUrl,
          logoScale: workspace?.logoScale, logoOffsetX: workspace?.logoOffsetX, logoOffsetY: workspace?.logoOffsetY,
          backgroundOffsetX: post.backgroundOffsetX ?? 0,
          backgroundOffsetY: post.backgroundOffsetY ?? 0,
          backgroundScale: post.backgroundScale ?? 1.0,
          ...buildBrandRenderOptions(templateForPost, post),
        }
      );
      if (thumbnailUrl) {
        thumbnails[post.id] = thumbnailUrl;
      }
    }
    setCompositeThumbnails((prev) => ({ ...prev, ...thumbnails }));
  }, [posts, postTemplateOverrides, buildDynamicTextsMap, workspaceLogoUrl, workspace?.logoScale, getTemplateForPost, buildBrandRenderOptions]);

  const handlePostTemplateChange = useCallback(async (postId: string, newTemplateId: string) => {
    if (newTemplateId) {
      setPostTemplateOverrides((prev) => ({ ...prev, [postId]: newTemplateId }));
      try {
        const template = await apiClient.get<Template>(`/api/workspaces/${workspaceId}/templates/${newTemplateId}`);
        await updateSingleThumbnail(postId, template, true);
      } catch (err) {
        console.error('Failed to update thumbnail:', err);
      }
    } else {
      setPostTemplateOverrides((prev) => {
        const n = { ...prev };
        delete n[postId];
        return n;
      });
      if (bulkTemplate && bulkTemplateId) {
        await updateSingleThumbnail(postId, bulkTemplate);
      } else {
        setCompositeThumbnails((prev) => {
          const n = { ...prev };
          delete n[postId];
          return n;
        });
      }
    }
  }, [workspaceId, bulkTemplate, bulkTemplateId, updateSingleThumbnail]);

  // Fetch bulk template when ID changes
  useEffect(() => {
    const fetchBulkTemplate = async () => {
      if (!bulkTemplateId) {
        setBulkTemplate(null);
        setCompositeThumbnails((prev) => {
          const n: Record<string, string> = {};
          Object.keys(postTemplateOverrides).forEach((postId) => {
            if (prev[postId]) n[postId] = prev[postId];
          });
          return n;
        });
        return;
      }
      try {
        const data = await apiClient.get<Template>(`/api/workspaces/${workspaceId}/templates/${bulkTemplateId}`);
        setBulkTemplate(data);
        await generateThumbnailPreviews(data);
      } catch (err) {
        console.error('Failed to fetch bulk template:', err);
      }
    };
    fetchBulkTemplate();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bulkTemplateId, workspaceId]);

  // Regenerate thumbnails when posts change
  useEffect(() => {
    if (bulkTemplate && posts.length > 0) {
      generateThumbnailPreviews(bulkTemplate);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [posts.length]);

  const handlePreviewSaveOffset = useCallback(async (postId: string, offsetX: number, offsetY: number, scale: number) => {
    try {
      await apiClient.patch(`/api/workspaces/${workspaceId}/posts/${postId}`, {
        backgroundOffsetX: offsetX,
        backgroundOffsetY: offsetY,
        backgroundScale: scale,
      });
      // Update local posts state
      setPosts?.(prevPosts => prevPosts.map(p =>
        p.id === postId
          ? { ...p, backgroundOffsetX: offsetX, backgroundOffsetY: offsetY, backgroundScale: scale }
          : p
      ));
    } catch (err) {
      console.error('Failed to save offset:', err);
      setError('Failed to save image position');
    }

    const templateId = postTemplateOverrides[postId] || bulkTemplateId;
    if (templateId) {
      try {
        const post = posts.find(p => p.id === postId);
        if (post && post.imageUrl) {
          const templateForPost = postTemplateOverrides[postId]
            ? await apiClient.get<Template>(`/api/workspaces/${workspaceId}/templates/${postTemplateOverrides[postId]}`)
            : bulkTemplate ? getTemplateForPost(post, bulkTemplate) : null;
          if (templateForPost) {
            const dynamicTexts = buildDynamicTextsMap(postId, templateForPost);
            const thumbnailUrl = await renderCompositeThumbnail(
              post.imageUrl, templateForPost, 400,
              { dynamicTexts, workspaceLogoUrl, logoScale: workspace?.logoScale, logoOffsetX: workspace?.logoOffsetX, logoOffsetY: workspace?.logoOffsetY, backgroundOffsetX: offsetX, backgroundOffsetY: offsetY, backgroundScale: scale, ...buildBrandRenderOptions(templateForPost, post) }
            );
            if (thumbnailUrl) {
              setCompositeThumbnails(prev => ({ ...prev, [postId]: thumbnailUrl }));
            }
          }
        }
      } catch (err) {
        console.error('Failed to regenerate thumbnail after offset change:', err);
      }
    }
  }, [workspaceId, postTemplateOverrides, bulkTemplateId, bulkTemplate, posts, buildDynamicTextsMap, workspaceLogoUrl, workspace?.logoScale, getTemplateForPost, buildBrandRenderOptions]);

  const handleTemplateCreated = useCallback((newTemplate: Template) => {
    const newSummary: TemplateSummary = {
      id: newTemplate.id,
      workspaceId: newTemplate.workspaceId,
      name: newTemplate.name,
      description: newTemplate.description,
      canvasWidth: newTemplate.canvasWidth,
      canvasHeight: newTemplate.canvasHeight,
      backgroundColor: newTemplate.backgroundColor,
      isDefault: newTemplate.isDefault,
      elementCount: newTemplate.elements.length,
      createdAt: newTemplate.createdAt,
      updatedAt: newTemplate.updatedAt,
    };
    setTemplates(prev => [...prev, newSummary]);
  }, []);

  const handleBulkApplyAndDownload = useCallback(async (quality: ExportQuality = 'mobile') => {
    if (!bulkTemplate || posts.length === 0) return;

    const postsWithImages = posts.filter((p) => p.imageUrl);
    if (postsWithImages.length === 0) return;

    const preset = EXPORT_PRESETS[quality];

    setIsBulkProcessing(true);
    setBulkProgress({ current: 0, total: postsWithImages.length });

    const zip = new JSZip();
    const imageFilenameByPostId: Record<string, string> = {};
    const imagePublicUrlByPostId: Record<string, string> = {};

    try {
      // 1. Render images with templates, add to ZIP, and upload to R2 in parallel
      for (let i = 0; i < postsWithImages.length; i++) {
        const post = postsWithImages[i];
        setBulkProgress({ current: i + 1, total: postsWithImages.length });
        const templateForPost = getTemplateForPost(post, bulkTemplate);
        const dynamicTexts = buildDynamicTextsMap(post.id, templateForPost);
        const blob = await renderCompositeAsBlob(
          post.imageUrl!, templateForPost,
          { dynamicTexts, workspaceLogoUrl, logoScale: workspace?.logoScale, logoOffsetX: workspace?.logoOffsetX, logoOffsetY: workspace?.logoOffsetY, scale: preset.scale, ...buildBrandRenderOptions(templateForPost, post) },
          preset.format,
          preset.quality
        );
        const filename = `${post.network}-${i + 1}.${preset.extension}`;
        if (blob) {
          zip.file(filename, blob);
          imageFilenameByPostId[post.id] = filename;

          // Upload composite to R2 so the CSV can reference a real public URL.
          // If upload fails (R2 not configured, network error, etc.), we silently
          // fall back to the local filename so the ZIP is still self-sufficient.
          try {
            const formData = new FormData();
            formData.append('file', blob, filename);
            const result = await apiClient.upload<{ url: string }>(
              `/api/workspaces/${workspaceId}/posts/${post.id}/upload-composite`,
              formData,
            );
            if (result?.url) {
              imagePublicUrlByPostId[post.id] = result.url;
            }
          } catch (uploadErr) {
            console.warn(`R2 upload failed for post ${post.id}, will use local filename in CSV:`, uploadErr);
          }
        }
      }

      // 2. Build CSV in social-media-posting format. Prefer public R2 URL, fall back to local filename.
      const csvEscape = (val: string): string => {
        if (!val) return '';
        if (val.includes(',') || val.includes('"') || val.includes('\n') || val.includes('\r')) {
          return `"${val.replace(/"/g, '""')}"`;
        }
        return val;
      };

      const csvLines: string[] = [
        'postAtSpecificTime (YYYY-MM-DD HH:mm:ss),content,link (OGmetaUrl),imageUrls,gifUrl,videoUrls',
      ];

      for (const post of posts) {
        const content = csvEscape(post.content);
        const imageRef = imagePublicUrlByPostId[post.id] || imageFilenameByPostId[post.id] || '';
        csvLines.push(`,${content},,${csvEscape(imageRef)},,`);
      }

      zip.file('social-media-posting.csv', csvLines.join('\n'));

      // 3. Download ZIP
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(zipBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${workspace?.name || 'posts'}-export-${quality}.zip`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to create ZIP:', err);
      setError('Failed to create ZIP file');
    } finally {
      setIsBulkProcessing(false);
      setBulkProgress({ current: 0, total: 0 });
    }
  }, [bulkTemplate, posts, workspaceId, workspace?.name, workspaceLogoUrl, workspace?.logoScale, buildDynamicTextsMap, getTemplateForPost, buildBrandRenderOptions]);

  return {
    templates,
    setTemplates,
    bulkTemplateId,
    setBulkTemplateId,
    bulkTemplate,
    solidTemplate,
    templatePool,
    postTemplateOverrides,
    setPostTemplateOverrides,
    postFormatOverrides,
    setPostFormatOverrides,
    compositeThumbnails,
    setCompositeThumbnails,
    workspaceLogoUrl,
    brandColors,
    brandFonts,
    buildBrandRenderOptions,
    isBulkProcessing,
    bulkProgress,
    templateError: error,
    fetchTemplates,
    fetchWorkspaceLogo,
    fetchBrandInfo,
    getDynamicTextForElement,
    buildDynamicTextsMap,
    getTemplateForPost,
    updateSingleThumbnail,
    generateThumbnailPreviews,
    handlePostTemplateChange,
    handlePreviewSaveOffset,
    handleTemplateCreated,
    handleBulkApplyAndDownload,
  };
}
